import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  EnvelopeSimple,
  ArrowUpRight,
  CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { SERVICES as INTERESTS, isService } from "@/lib/inquiry";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MESSAGE_PLACEHOLDERS = {
  website:
    "A quick site for my pressure washing business — quote form, gallery, and a few service pages.",
  website_improve:
    "My homepage looks dated on phones and the quote form is hard to use. I'd like better service pages too.",
  local_visibility:
    "I want my Google listing cleaned up and four posts going out every month to Google and Facebook. I can send photos from jobs.",
  gbp_setup:
    "My Google listing is incomplete and the hours and categories are wrong. I'd like it cleaned up — one location, no monthly plan for now.",
  automations:
    "I'd like customers to get an instant confirmation when they submit my quote form, plus a text back when I miss a call.",
  multiple:
    "I need a new site and I'd like my Google profile handled monthly too.",
};

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  business: "",
  interest: "website",
  site_url: "",
  message: "",
  website: "",
};

export default function Contact({ defaultInterest = "website" }) {
  const [form, setForm] = useState({
    ...EMPTY_FORM,
    interest: isService(defaultInterest) ? defaultInterest : "website",
  });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [urlError, setUrlError] = useState("");
  const needsUrl = form.interest === "website_improve";
  // Automations and multi-service inquiries need a compatibility check, so the
  // URL helps — but it isn't required there.
  const wantsUrl =
    needsUrl || form.interest === "automations" || form.interest === "multiple";

  // Standard, non-strict email format check — bypasses browser-native
  // validation on purpose so we control the UX and messaging.
  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const onInterest = (e) => {
      if (isService(e.detail)) {
        setSubmitted(false);
        setForm((f) => ({ ...f, interest: e.detail }));
      }
    };
    window.addEventListener("webjesters:interest", onInterest);
    return () => window.removeEventListener("webjesters:interest", onInterest);
  }, []);

  // Arriving from another page via /?service=automations#contact
  useEffect(() => {
    const requested = new URLSearchParams(window.location.search).get("service");
    if (isService(requested)) {
      setForm((f) => ({ ...f, interest: requested }));
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    if (!isValidEmail(form.email)) {
      setEmailError("Please enter a valid email address.");
      toast.error("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    if (needsUrl && !form.site_url.trim()) {
      setUrlError("Add your website address so we can review it.");
      toast.error("Please add your website address.");
      return;
    }
    setUrlError("");
    if (!consent) {
      toast.error("Please agree to receive emails and texts about your inquiry.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSubmitted(true);
      toast.success("Inquiry sent. Greg will reach out shortly.");
      // Reset honeypot too so a real user re-using the form can't accidentally
      // trigger suppression on a second submit.
      setForm((f) => ({ ...f, website: "" }));
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Could not send right now. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not send right now.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative pt-14 pb-20 sm:pt-28 sm:pb-24 md:pt-36 md:pb-32 bg-[#0c0c0d] border-t border-white/[0.06]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-14">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.22em] uppercase text-zinc-400 flex items-center gap-3">
              <span className="block w-6 h-px bg-zinc-600" />
              Get in touch
            </div>
            <h2 className="mt-5 font-display text-3xl sm:text-5xl lg:text-[54px] leading-[1.04] tracking-tight text-white">
              Tell us what
              <br />
              you need.
            </h2>
            <p className="mt-5 text-zinc-300 text-[15px] sm:text-base max-w-md leading-relaxed">
              A new website, improvements to the site you already have, a
              Google Business Profile setup, Local Visibility, or quote &amp;
              call automations — ask about any of it. We'll reply with a clear
              plan within one business day. No sales calls.
            </p>

            <div className="mt-8 grid gap-3">
              <a
                href="tel:6023905623"
                data-testid="contact-phone-link"
                className="group flex items-center justify-between p-4 rounded-2xl border border-white/[0.07] bg-[#101012] hover:border-white/[0.14] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/[0.04] border border-white/10">
                    <Phone size={16} weight="regular" className="text-white" />
                  </span>
                  <div>
                    <div className="text-[12px] text-zinc-400 uppercase tracking-[0.18em]">
                      Phone
                    </div>
                    <div className="text-white text-[15px]">602-390-5623</div>
                  </div>
                </div>
                <ArrowUpRight size={16} weight="bold" className="text-zinc-400 group-hover:text-white" />
              </a>

              <a
                href="mailto:team@webjesters.com"
                data-testid="contact-email-link"
                className="group flex items-center justify-between p-4 rounded-2xl border border-white/[0.07] bg-[#101012] hover:border-white/[0.14] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/[0.04] border border-white/10">
                    <EnvelopeSimple size={16} weight="regular" className="text-white" />
                  </span>
                  <div>
                    <div className="text-[12px] text-zinc-400 uppercase tracking-[0.18em]">
                      Email
                    </div>
                    <div className="text-white text-[15px]">team@webjesters.com</div>
                  </div>
                </div>
                <ArrowUpRight size={16} weight="bold" className="text-zinc-400 group-hover:text-white" />
              </a>
            </div>

            <p
              data-testid="contact-hours"
              className="mt-5 text-[13px] text-zinc-400 leading-relaxed"
            >
              Mon–Fri · 9:00am–5:00pm Arizona time. We reply to every inquiry
              within one business day.
            </p>
          </div>

          <motion.form
            onSubmit={submit}
            noValidate
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            data-testid="quote-form"
            className="lg:col-span-7 rounded-3xl border border-white/[0.07] bg-[#101012] p-6 sm:p-8 md:p-10 relative overflow-hidden"
          >
            {submitted ? (
              <SuccessState onReset={() => { setSubmitted(false); setConsent(false); setEmailError(""); setUrlError(""); setForm({ ...EMPTY_FORM }); }} />
            ) : (
              <>
                {/* Honeypot — visually hidden, off-screen, aria-hidden, tab-skipped.
                    Real users never see or fill this. Bots that auto-fill any
                    "website" field will trip the silent suppression on the backend. */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-10000px",
                    top: "auto",
                    width: "1px",
                    height: "1px",
                    overflow: "hidden",
                  }}
                >
                  <label htmlFor="website-url-field">
                    Website (leave blank)
                  </label>
                  <input
                    id="website-url-field"
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={update("website")}
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                  <Field
                    label="Your Name"
                    testid="form-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Greg Clark"
                    required
                  />
                  <Field
                    label="Email"
                    testid="form-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => {
                      update("email")(e);
                      if (emailError) setEmailError("");
                    }}
                    placeholder="you@example.com"
                    required
                    error={emailError}
                  />
                  <Field
                    label="Phone"
                    testid="form-phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(602) 390-5623"
                  />
                  <Field
                    label="Business Name"
                    testid="form-business"
                    value={form.business}
                    onChange={update("business")}
                    placeholder="CleanCurrent LLC"
                  />
                </div>

                <div className="mt-5">
                  <Label htmlFor="form-interest-website">I'm interested in</Label>
                  <div
                    id="form-interest"
                    role="radiogroup"
                    aria-label="I'm interested in"
                    data-testid="form-interest"
                    className="mt-2 flex flex-wrap gap-2"
                  >
                    {INTERESTS.map((opt) => {
                      const active = form.interest === opt.value;
                      return (
                        <button
                          key={opt.value}
                          id={`form-interest-${opt.value}`}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          data-testid={`form-interest-${opt.value}`}
                          onClick={() => {
                            setForm({ ...form, interest: opt.value });
                            if (opt.value !== "website_improve") setUrlError("");
                          }}
                          className={`px-4 py-2 rounded-full border text-[13px] transition-colors ${
                            active
                              ? "bg-white text-black border-white"
                              : "bg-[#0a0a0c] text-zinc-300 border-white/12 hover:border-white/30 hover:text-white"
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <AnimatePresence initial={false}>
                  {wantsUrl && (
                    <motion.div
                      key="site-url"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="mt-5">
                        <Field
                          label={
                            needsUrl
                              ? "Your Website Address"
                              : "Your Website Address (if you have one)"
                          }
                          testid="form-site-url"
                          type="url"
                          value={form.site_url}
                          onChange={(e) => {
                            update("site_url")(e);
                            if (urlError) setUrlError("");
                          }}
                          placeholder="yourbusiness.com"
                          required={needsUrl}
                          error={urlError}
                        />
                        <p className="mt-2 text-[12.5px] text-zinc-400">
                          {needsUrl
                            ? "We review your site and the platform it's built on before quoting anything."
                            : "Automations are optional and quoted individually — we check your site's forms and phone setup first to confirm what's compatible."}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="mt-5">
                  <Label htmlFor="form-message">
                    {needsUrl ? "What do you want improved?" : "What do you need?"}
                  </Label>
                  <textarea
                    id="form-message"
                    data-testid="form-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    placeholder={MESSAGE_PLACEHOLDERS[form.interest] || MESSAGE_PLACEHOLDERS.website}
                    className="mt-2 w-full bg-[#0a0a0c] border border-white/10 rounded-xl px-4 py-3 text-[14.5px] text-white placeholder-zinc-500 focus:outline-none focus:border-white/30 transition-colors"
                  />
                </div>

                <div className="mt-7">
                  <label
                    htmlFor="form-consent"
                    className="flex items-start gap-3 cursor-pointer select-none group"
                  >
                    <span className="relative flex-shrink-0 mt-0.5">
                      <input
                        id="form-consent"
                        type="checkbox"
                        required
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        data-testid="form-consent"
                        className="peer sr-only"
                      />
                      <span
                        aria-hidden="true"
                        className="
                          block h-[18px] w-[18px] rounded-[5px]
                          border border-white/20 bg-[#0a0a0c]
                          group-hover:border-white/40
                          peer-focus-visible:ring-2 peer-focus-visible:ring-white/30 peer-focus-visible:ring-offset-0
                          peer-checked:bg-white peer-checked:border-white
                          transition-colors duration-150
                        "
                      />
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 12 10"
                        className="
                          pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
                          h-[8px] w-[10px]
                          opacity-0 peer-checked:opacity-100
                          transition-opacity duration-150
                        "
                      >
                        <path
                          d="M1 5.2 4.3 8.5 11 1.5"
                          fill="none"
                          stroke="#0a0a0c"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span className="text-[13px] text-zinc-300 leading-relaxed group-hover:text-white">
                      I agree to receive emails and SMS text messages from
                      WebJesters about my inquiry. Consent isn&apos;t a condition
                      of purchase. Msg &amp; data rates may apply; frequency
                      varies. Reply STOP to opt out, HELP for help.
                    </span>
                  </label>
                </div>

                <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="text-[12.5px] text-zinc-400 leading-relaxed">
                    By submitting, you agree to WebJesters'{" "}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-200 underline underline-offset-4 hover:text-white"
                    >
                      Terms
                    </a>{" "}
                    and{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-200 underline underline-offset-4 hover:text-white"
                    >
                      Privacy Policy
                    </a>
                    .
                  </div>
                  <button
                    type="submit"
                    disabled={submitting || !consent}
                    data-testid="form-submit"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending…" : "Send inquiry"}
                    <ArrowUpRight size={16} weight="bold" />
                  </button>
                </div>
              </>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Label({ htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="text-[12px] tracking-[0.16em] uppercase text-zinc-400">
      {children}
    </label>
  );
}

function Field({ label, testid, value, onChange, placeholder, type = "text", required, error }) {
  const inputId = testid;
  const errorId = error ? `${testid}-error` : undefined;
  return (
    <div>
      <Label htmlFor={inputId}>{label}{required && <span className="text-brand-400 ml-1">*</span>}</Label>
      <input
        id={inputId}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        aria-invalid={error ? true : undefined}
        aria-describedby={errorId}
        className="mt-2 w-full bg-[#0a0a0c] border border-white/10 rounded-xl px-4 py-3 text-[14.5px] text-white placeholder-zinc-500 focus:outline-none focus:border-white/30 transition-colors"
      />
      {error && (
        <p
          id={errorId}
          role="alert"
          data-testid={`${testid}-error`}
          className="mt-2 text-[12.5px] text-red-400"
        >
          {error}
        </p>
      )}
    </div>
  );
}

function SuccessState({ onReset }) {
  return (
    <div data-testid="form-success" className="py-10 text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
        <CheckCircle size={26} weight="regular" />
      </div>
      <h3 className="mt-5 font-display text-3xl tracking-tight text-white">
        Got it — we'll be in touch.
      </h3>
      <p className="mt-3 text-zinc-300 max-w-md mx-auto text-[14.5px]">
        Greg reads every inquiry personally. Expect a clear, no-pressure
        reply within one business day.
      </p>
      <button
        type="button"
        onClick={onReset}
        className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/10 bg-white/[0.03] text-white hover:bg-brand-700/25 hover:border-brand-500/50 transition-colors text-[13.5px]"
      >
        Send another inquiry
      </button>
    </div>
  );
}
